import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FaArrowLeft, FaCcMastercard, FaCcVisa, FaCheckCircle, FaCreditCard, FaFemale, FaTimesCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Cropper from 'react-easy-crop';
import { getCardApplicationFee, getWomenTrainingFee, initiateWomenTrainingPayment } from '../../../api/cardService';
import { initiateGeneralPayment } from '../../../api/paymentService';
import './CardApplicationForm.scss';

const initialFormData = {
  firstName: '',
  lastName: '',
  identityNumber: '',
  email: '',
  phone: '',
  phone2: '',
  trainingType: '',
  userGender: 'Female',
  profession: '',
  homeAddress: '',
  birthDate: '',
  photoBase64: '',
  termsAccepted: false
};

const initialPaymentData = {
  cardNumber: '',
  cardName: '',
  expiryMonth: '',
  expiryYear: '',
  cvv: ''
};

const formatVatIncluded = (amount) => `${Number(amount || 0).toFixed(2)} TL (KDV dahil)`;

const WomenTrainingApplicationForm = () => {
  const [step, setStep] = useState(1);
  const [cardFee, setCardFee] = useState(50.0);
  const [trainingFee, setTrainingFee] = useState(900.0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [paymentData, setPaymentData] = useState(initialPaymentData);
  const [paymentErrors, setPaymentErrors] = useState({});
  const [paymentHtml, setPaymentHtml] = useState(null);
  const [paymentOutcome, setPaymentOutcome] = useState(null);
  const [resultMessage, setResultMessage] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const iframeRef = useRef(null);

  // Crop States
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);

  const totalAmount = cardFee + trainingFee;

  useEffect(() => {
    const fetchFees = async () => {
      const [cardRes, trainingRes] = await Promise.all([
        getCardApplicationFee(),
        getWomenTrainingFee()
      ]);
      if (cardRes?.isSuccess && cardRes.data?.fee) setCardFee(cardRes.data.fee);
      if (trainingRes?.isSuccess && trainingRes.data?.fee) setTrainingFee(trainingRes.data.fee);
    };
    fetchFees();
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (!data || data.type !== 'PAYMENT_RESULT') return;
      if (data.status === 'success') {
        setPaymentOutcome('success');
        setResultMessage('Kadın eğitim başvurunuz başarıyla tamamlandı.');
        setPaymentHtml(null);
      } else if (data.status === 'failed') {
        setPaymentOutcome('failed');
        setResultMessage(data.error ? decodeURIComponent(data.error) : 'Ödeme işlemi başarısız oldu.');
        setPaymentHtml(null);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const href = iframe.contentWindow?.location?.href;
      if (!href) return;
      const iframeUrl = new URL(href);
      const paymentStatus = iframeUrl.searchParams.get('paymentStatus');
      const error = iframeUrl.searchParams.get('error');
      if (paymentStatus === 'success') {
        setPaymentOutcome('success');
        setResultMessage('Kadın eğitim başvurunuz başarıyla tamamlandı.');
        setPaymentHtml(null);
      } else if (paymentStatus === 'failed') {
        setPaymentOutcome('failed');
        setResultMessage(error ? decodeURIComponent(error) : 'Ödeme işlemi başarısız oldu.');
        setPaymentHtml(null);
      }
    } catch { }
  };

  const luhnCheck = (value) => {
    let checksum = 0;
    let multiplier = 1;
    for (let i = value.length - 1; i >= 0; i -= 1) {
      let product = Number(value.charAt(i)) * multiplier;
      if (product > 9) { checksum += 1; product -= 10; }
      checksum += product;
      multiplier = multiplier === 1 ? 2 : 1;
    }
    return checksum % 10 === 0;
  };

  const detectCardType = (cardNumber) => {
    if (!cardNumber) return null;
    const cleaned = cardNumber.replace(/\s/g, '');
    if (cleaned.startsWith('4')) return '1';
    if (/^5[1-5]/.test(cleaned) || /^2[2-7]/.test(cleaned)) return '2';
    if (/^(9792|65|36|2205)/.test(cleaned)) return '3';
    return null;
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    canvas.width = image.width;
    canvas.height = image.height;
    ctx.translate(image.width / 2, image.height / 2);
    ctx.translate(-image.width / 2, -image.height / 2);
    ctx.drawImage(image, 0, 0);
    const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    ctx.putImageData(data, 0, 0);
    return canvas.toDataURL('image/jpeg');
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropSave = async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      setFormData({ ...formData, photoBase64: croppedImage });
      setIsCropping(false);
      setImageSrc(null);
    } catch (e) {
      console.error(e);
      toast.error('Fotoğraf kırpılırken bir hata oluştu.');
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Fotoğraf boyutu en fazla 5MB olmalıdır.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageSrc(reader.result);
        setIsCropping(true);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = null;
  };

  const handleContinueToPayment = () => {
    if (!formData.firstName || !formData.lastName || !formData.identityNumber || !formData.phone || !formData.userGender || !formData.profession || !formData.homeAddress || !formData.birthDate || !formData.trainingType || !formData.photoBase64) {
      toast.error('Lütfen tüm zorunlu alanları ve fotoğrafı doldurunuz.');
      return;
    }
    if (!formData.termsAccepted) {
      toast.error('Lütfen tesis kullanma talimatını onaylayınız.');
      return;
    }
    if (formData.identityNumber.length !== 11) {
      toast.error('TC Kimlik No 11 haneli olmalıdır.');
      return;
    }
    if (formData.phone.length !== 10 || !formData.phone.startsWith('5')) {
      toast.error('Geçerli bir telefon numarası giriniz (5XXXXXXXXX).');
      return;
    }
    setStep(2);
  };

  const handlePayment = async () => {
    setLoading(true);
    const errors = {};
    const pan = paymentData.cardNumber.replace(/\s/g, '');
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    if (!pan) errors.cardNumber = 'Kart numarası gereklidir.';
    else if (pan.length !== 16) errors.cardNumber = 'Kart numarası 16 haneli olmalıdır.';
    else if (!luhnCheck(pan)) errors.cardNumber = 'Geçersiz kart numarası.';
    if (!paymentData.cardName.trim()) errors.cardName = 'Kart üzerindeki isim gereklidir.';
    if (!paymentData.expiryMonth || !paymentData.expiryYear) errors.expiry = 'Son kullanma tarihi seçiniz.';
    else {
      const ey = parseInt(paymentData.expiryYear, 10);
      const em = parseInt(paymentData.expiryMonth, 10);
      if (ey < currentYear || (ey === currentYear && em < currentMonth)) errors.expiry = 'Kartın son kullanma tarihi geçmiş.';
    }
    if (!paymentData.cvv) errors.cvv = 'CVV gereklidir.';
    else if (paymentData.cvv.length !== 3) errors.cvv = 'CVV 3 haneli olmalıdır.';

    if (Object.keys(errors).length > 0) {
      setPaymentErrors(errors);
      setLoading(false);
      return;
    }
    setPaymentErrors({});

    try {
      const initResult = await initiateWomenTrainingPayment({
        firstName: formData.firstName,
        lastName: formData.lastName,
        identityNumber: formData.identityNumber,
        email: formData.email || '',
        phone: formData.phone,
        phone2: formData.phone2,
        userGender: formData.userGender,
        trainingType: formData.trainingType,
        profession: formData.profession,
        homeAddress: formData.homeAddress,
        birthDate: formData.birthDate,
        photoBase64: formData.photoBase64,
        pan,
        cv2: paymentData.cvv,
        expireMonth: paymentData.expiryMonth.padStart(2, '0'),
        expireYear: paymentData.expiryYear,
        cardType: detectCardType(paymentData.cardNumber)
      });

      if (!initResult?.isSuccess) {
        toast.error(initResult?.resultMessage || 'Başvuru oluşturulamadı.');
        setLoading(false);
        return;
      }

      const paymentPayload = {
        amount: initResult.data.totalAmount || totalAmount,
        paymentGroup: 'WomenTrainingFee',
        referenceId: String(initResult.data.applicationId),
        applicationId: initResult.data.applicationId,
        description: initResult.data.description || `Kadın Eğitim Başvurusu - ${formData.firstName} ${formData.lastName}`,
        pan,
        cv2: paymentData.cvv,
        expireMonth: paymentData.expiryMonth.padStart(2, '0'),
        expireYear: paymentData.expiryYear,
        cardType: detectCardType(paymentData.cardNumber),
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || ''
      };

      const paymentResult = await initiateGeneralPayment(paymentPayload);
      if (paymentResult?.isSuccess && paymentResult?.data?.html) {
        setPaymentHtml(paymentResult.data.html);
        setStep(3);
      } else {
        toast.error(paymentResult?.resultMessage || 'Ödeme başlatılamadı.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Ödeme sırasında beklenmeyen bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setFormData(initialFormData);
    setPaymentData(initialPaymentData);
    setPaymentErrors({});
    setPaymentHtml(null);
    setPaymentOutcome(null);
    setResultMessage('');
    setResetKey(prev => prev + 1);
  };

  const previewCardNumber = paymentData.cardNumber || '0000 0000 0000 0000';
  const fallbackCardHolder = `${formData.firstName} ${formData.lastName}`.trim();
  const previewCardHolder = paymentData.cardName || fallbackCardHolder || 'AD SOYAD';
  const previewExpiry = paymentData.expiryMonth && paymentData.expiryYear
    ? `${paymentData.expiryMonth}/${String(paymentData.expiryYear).slice(-2)}`
    : 'AA/YY';

  const cropModalOverlayStyle = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center' };
  const cropModalStyle = { width: '90%', maxWidth: '500px', height: '70vh', background: '#fff', borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column' };
  const cropControlsStyle = { display: 'flex', alignItems: 'center', padding: '12px 16px', gap: '10px', borderTop: '1px solid #e2e8f0' };
  const cropBtnCancel = { padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontWeight: '600' };
  const cropBtnSave = { padding: '8px 16px', border: 'none', borderRadius: '8px', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontWeight: '600' };

  return (
    <div className="sa-card-application sa-women-training sa-unified-mode">
      <div className="sa-facility-details-header sa-unified-tab-header">
        <div className="sa-unified-tab-header-row">
          <h3 className="sa-unified-tab-title">
            <div className="sa-unified-tab-icon" style={{ background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)' }}>
              <FaFemale size={20} color="#fff" />
            </div>
            Kadın Eğitim Başvuru Formu
          </h3>
          {step === 1 && (
            <div className="sa-unified-tab-price-breakdown">
              <span className="sa-price-item">Kart: {formatVatIncluded(cardFee)}</span>
              <span className="sa-price-separator">+</span>
              <span className="sa-price-item">Eğitim: {formatVatIncluded(trainingFee)}</span>
              <span className="sa-price-total">Top: {formatVatIncluded(totalAmount)}</span>
            </div>
          )}
        </div>
        <p className="sa-unified-tab-subtitle">
          Kadın eğitim programı başvurunuzu tamamlamak için bilgilerinizi doldurup ödemenizi yapınız.
        </p>
      </div>

      {/* Uyarı Kutusu */}
      <div className="sa-women-training-notice">
        <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>⚠️</span>
        <div>
          <strong style={{ color: '#92400e', display: 'block', marginBottom: '4px' }}>Önemli Bilgilendirme</strong>
          <p style={{ color: '#78350f', margin: 0, fontSize: '0.88rem', lineHeight: '1.6' }}>
            Bu ödeme yalnızca <strong>kadın eğitim dersleri</strong> içindir. Lütfen buna göre ödeme yapınız.<br />
            <strong>Ödeme sonrası iade yapılmamaktadır.</strong>
          </p>
        </div>
      </div>

      <div className="sa-women-training-content">
        <div className="sa-stepper sa-card-stepper" style={{ marginBottom: '30px' }}>
          <div className={`sa-step ${step === 1 ? 'active' : step > 1 ? 'completed' : ''}`}>
            <span className="sa-step-circle">1</span>
            <span className="sa-step-label">Bilgiler</span>
            <div className="sa-step-line"></div>
          </div>
          <div className={`sa-step ${step === 2 ? 'active' : step > 2 ? 'completed' : ''}`}>
            <span className="sa-step-circle">2</span>
            <span className="sa-step-label">Ödeme</span>
            <div className="sa-step-line"></div>
          </div>
          <div className={`sa-step ${step >= 3 ? 'active' : ''}`}>
            <span className="sa-step-circle">3</span>
            <span className="sa-step-label">Sonuç</span>
          </div>
        </div>

        {step === 1 ? (
          <>
            <div className="sa-appointment-section">
              <div className="sa-booking-form sa-card-form" style={{ marginTop: '20px', border: 'none', background: 'transparent', boxShadow: 'none', padding: '10px 12px 14px' }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ width: '24px', height: '3px', background: '#e2e8f0', borderRadius: '2px', display: 'block' }}></span>
                  Başvuru Bilgileri
                </h4>
                <div className="sa-form-grid">
                  <div className="sa-form-group">
                    <label>Ad <span className="sa-required">*</span></label>
                    <input className="sa-form-input" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} />
                  </div>
                  <div className="sa-form-group">
                    <label>Soyad <span className="sa-required">*</span></label>
                    <input className="sa-form-input" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} />
                  </div>
                  <div className="sa-form-group">
                    <label>TC Kimlik No <span className="sa-required">*</span></label>
                    <input className="sa-form-input" maxLength={11} value={formData.identityNumber}
                      onChange={(e) => setFormData({ ...formData, identityNumber: e.target.value.replace(/\D/g, '').slice(0, 11) })} />
                  </div>
                  <div className="sa-form-group">
                    <label>Telefon <span className="sa-required">*</span></label>
                    <input className="sa-form-input" value={formData.phone} maxLength={10} placeholder="5XXXXXXXXX"
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} />
                  </div>
                  <div className="sa-form-group">
                    <label>Cep Telefonu 2 (Opsiyonel)</label>
                    <input className="sa-form-input" value={formData.phone2} maxLength={10} placeholder="5XXXXXXXXX"
                      onChange={(e) => setFormData({ ...formData, phone2: e.target.value.replace(/\D/g, '').slice(0, 10) })} />
                  </div>
                  <div className="sa-form-group">
                    <label>E-posta</label>
                    <input className="sa-form-input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                  </div>
                  <div className="sa-form-group">
                    <label>Cinsiyet <span className="sa-required">*</span></label>
                    <select className="sa-form-input" value={formData.userGender} onChange={(e) => setFormData({ ...formData, userGender: e.target.value })}>
                      <option value="">Seçiniz</option>
                      <option value="Female">Kadın</option>
                    </select>
                  </div>
                  <div className="sa-form-group">
                    <label>Meslek <span className="sa-required">*</span></label>
                    <input className="sa-form-input" value={formData.profession} onChange={(e) => setFormData({ ...formData, profession: e.target.value })} />
                  </div>
                  <div className="sa-form-group">
                    <label>Eğitim Türü <span className="sa-required">*</span></label>
                    <select className="sa-form-input" value={formData.trainingType} onChange={(e) => setFormData({ ...formData, trainingType: e.target.value })}>
                      <option value="">Seçiniz</option>
                      <option value="Temel Eğitim">Temel Eğitim</option>
                      <option value="İleri Seviye">İleri Seviye</option>
                    </select>
                  </div>
                  <div className="sa-form-group">
                    <label>Doğum Tarihi <span className="sa-required">*</span></label>
                    <input type="date" key={`dob-${resetKey}`} className="sa-form-input" defaultValue={formData.birthDate} max="9999-12-31"
                      onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })} />
                  </div>
                  <div className="sa-form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Ev Adresi <span className="sa-required">*</span></label>
                    <textarea className="sa-form-input" rows="3" value={formData.homeAddress}
                      onChange={(e) => setFormData({ ...formData, homeAddress: e.target.value })} style={{ resize: 'vertical' }}></textarea>
                  </div>
                  <div className="sa-form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Vesikalık / Profil Fotoğrafı <span className="sa-required">*</span></label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      {formData.photoBase64 && (
                        <img src={formData.photoBase64} alt="Preview" style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #ec4899' }} />
                      )}
                      <input type="file" accept="image/*" onChange={handlePhotoUpload} className="sa-form-input" style={{ flex: 1, padding: '8px' }} />
                    </div>
                    <small style={{ color: '#64748b', display: 'block', marginTop: '5px' }}>Lütfen yüzünüzün net göründüğü bir fotoğraf yükleyiniz (Maks 5MB).</small>
                  </div>
                  <div className="sa-form-group" style={{ gridColumn: '1 / -1', marginTop: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                      <input type="checkbox" id="womenTermsAccepted" checked={formData.termsAccepted}
                        onChange={(e) => setFormData({ ...formData, termsAccepted: e.target.checked })}
                        style={{ width: '20px', height: '20px', cursor: 'pointer' }} />
                      <label htmlFor="womenTermsAccepted" style={{ fontSize: '0.9rem', color: '#334155', cursor: 'pointer', fontWeight: '600' }}>
                        <span onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }} style={{ color: '#ec4899', textDecoration: 'underline' }}>
                          Tesis Kullanma Talimatı
                        </span>
                        'nı okudum ve onaylıyorum.
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="sa-action-row">
                <button type="button" className="sa-submit-button" onClick={handleContinueToPayment} style={{ background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)' }}>
                  Ödemeye Geç
                </button>
              </div>

              {isCropping && (
                <div style={cropModalOverlayStyle}>
                  <div style={cropModalStyle}>
                    <div style={{ position: 'relative', width: '100%', height: 'calc(100% - 60px)', background: '#333' }}>
                      <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={3 / 4}
                        onCropChange={setCrop} onCropComplete={onCropComplete} onZoomChange={setZoom} />
                    </div>
                    <div style={cropControlsStyle}>
                      <input type="range" value={zoom} min={1} max={3} step={0.1} onChange={(e) => setZoom(e.target.value)} style={{ flex: 1, margin: '0 10px' }} />
                      <button onClick={(e) => { e.preventDefault(); setIsCropping(false); setImageSrc(null); }} style={cropBtnCancel}>İptal</button>
                      <button onClick={(e) => { e.preventDefault(); handleCropSave(); }} style={cropBtnSave}>Kırp ve Kaydet</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : step === 2 ? (
          <div className="sa-appointment-section">
            <div className="sa-booking-form sa-card-form" style={{ marginTop: '20px', border: 'none', background: 'transparent', boxShadow: 'none', padding: '10px 12px 14px' }}>
              <h4 className="sa-modern-section-title">
                <span className="sa-modern-section-line"></span>
                Ödeme Bilgileri
              </h4>
              <div className="sa-modern-payment-shell sa-card-payment-shell">
                <div className="sa-modern-payment-meta">
                  <div className="sa-modern-payment-meta-item">
                    <span className="sa-modern-payment-meta-label">Ücret Detayı</span>
                    <strong className="sa-modern-payment-meta-value" style={{ fontSize: '0.85rem' }}>
                      Kart: {formatVatIncluded(cardFee)} + Eğitim: {formatVatIncluded(trainingFee)}
                    </strong>
                  </div>
                  <div className="sa-modern-payment-meta-item sa-modern-payment-meta-item--amount">
                    <span className="sa-modern-payment-meta-label">Ödenecek Toplam</span>
                    <strong className="sa-modern-payment-meta-value">{formatVatIncluded(totalAmount)}</strong>
                  </div>
                </div>
                <div className="sa-modern-payment-layout">
                  <div className="sa-modern-card-preview sa-modern-card-preview--card-app" style={{ background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)' }}>
                    <span className="sa-modern-card-chip" aria-hidden="true"></span>
                    <div className="sa-modern-card-number">{previewCardNumber}</div>
                    <div className="sa-modern-card-footer">
                      <div><span>Kart Sahibi</span><strong>{previewCardHolder}</strong></div>
                      <div className="sa-modern-card-expiry"><span>SKT</span><strong>{previewExpiry}</strong></div>
                    </div>
                    <div className="sa-modern-card-brand">
                      {detectCardType(paymentData.cardNumber) === '1' && <FaCcVisa size={28} color="#f8fafc" />}
                      {detectCardType(paymentData.cardNumber) === '2' && <FaCcMastercard size={28} color="#fed7aa" />}
                      {detectCardType(paymentData.cardNumber) === '3' && (
                        <span style={{ fontWeight: 800, fontSize: '13px', letterSpacing: '0.8px', background: '#005ba9', color: '#fff', padding: '2px 7px', borderRadius: '4px', fontStyle: 'italic', display: 'inline-flex', alignItems: 'center' }}>
                          TROY
                        </span>
                      )}
                      {!detectCardType(paymentData.cardNumber) && <FaCreditCard size={24} color="#dbeafe" />}
                    </div>
                  </div>
                  <div className="sa-modern-payment-form">
                    <div className="sa-form-grid sa-modern-payment-grid">
                      <div className="sa-form-group sa-modern-payment-wide">
                        <label>Kart Numarası</label>
                        <div className="sa-input-with-icon">
                          <input className="sa-form-input" value={paymentData.cardNumber} maxLength={19} inputMode="numeric" placeholder="0000 0000 0000 0000"
                            onChange={(e) => {
                              const raw = e.target.value.replace(/\D/g, '').slice(0, 16);
                              const formatted = raw.match(/.{1,4}/g)?.join(' ') || raw;
                              setPaymentData({ ...paymentData, cardNumber: formatted });
                            }} />
                          <div className="sa-card-brand-icon">
                            {detectCardType(paymentData.cardNumber) === '1' && <FaCcVisa size={24} color="#1a1f71" />}
                            {detectCardType(paymentData.cardNumber) === '2' && <FaCcMastercard size={24} color="#f97316" />}
                            {detectCardType(paymentData.cardNumber) === '3' && (
                              <span style={{ fontWeight: 800, fontSize: '11px', letterSpacing: '0.5px', background: '#005ba9', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontStyle: 'italic', display: 'inline-flex', alignItems: 'center' }}>
                                TROY
                              </span>
                            )}
                            {!detectCardType(paymentData.cardNumber) && <FaCreditCard size={22} color="#94a3b8" />}
                          </div>
                        </div>
                        {paymentErrors.cardNumber && <div className="sa-error-message">{paymentErrors.cardNumber}</div>}
                      </div>
                      <div className="sa-form-group sa-modern-payment-wide">
                        <label>Kart Üzerindeki İsim</label>
                        <input className="sa-form-input" value={paymentData.cardName} placeholder="AD SOYAD"
                          onChange={(e) => setPaymentData({ ...paymentData, cardName: e.target.value.toUpperCase() })} />
                        {paymentErrors.cardName && <div className="sa-error-message">{paymentErrors.cardName}</div>}
                      </div>
                      <div className="sa-form-group">
                        <label>Ay</label>
                        <select className="sa-form-input" value={paymentData.expiryMonth} onChange={(e) => setPaymentData({ ...paymentData, expiryMonth: e.target.value })}>
                          <option value="">Ay</option>
                          {Array.from({ length: 12 }, (_, i) => { const m = String(i + 1).padStart(2, '0'); return <option key={m} value={m}>{m}</option>; })}
                        </select>
                      </div>
                      <div className="sa-form-group">
                        <label>Yıl</label>
                        <select className="sa-form-input" value={paymentData.expiryYear} onChange={(e) => setPaymentData({ ...paymentData, expiryYear: e.target.value })}>
                          <option value="">Yıl</option>
                          {Array.from({ length: 10 }, (_, i) => { const y = String(new Date().getFullYear() + i); return <option key={y} value={y}>{y}</option>; })}
                        </select>
                      </div>
                      <div className="sa-form-group">
                        <label>CVV</label>
                        <input className="sa-form-input" type="password" maxLength={3} inputMode="numeric" value={paymentData.cvv} placeholder="123"
                          onChange={(e) => setPaymentData({ ...paymentData, cvv: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
                        {paymentErrors.cvv && <div className="sa-error-message">{paymentErrors.cvv}</div>}
                      </div>
                    </div>
                    {paymentErrors.expiry && <div className="sa-error-message">{paymentErrors.expiry}</div>}
                  </div>
                </div>
              </div>
              <div className="sa-action-row sa-card-action-row">
                <button type="button" className="sa-btn-cancel" onClick={() => { setStep(1); setPaymentData(initialPaymentData); setPaymentErrors({}); }} disabled={loading}>Geri</button>
                <button type="button" className="sa-submit-button" onClick={handlePayment} disabled={loading} style={{ background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)' }}>
                  {loading ? 'İşleniyor...' : 'Ödemeyi Tamamla'}
                </button>
              </div>
            </div>
          </div>
        ) : paymentOutcome ? (
          <div className={`sa-payment-result sa-card-result ${paymentOutcome === 'success' ? 'sa-payment-success' : 'sa-payment-failed'}`}>
            <div className={`sa-result-icon ${paymentOutcome === 'failed' ? 'error' : ''}`}>
              {paymentOutcome === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
            </div>
            <h2>{paymentOutcome === 'success' ? 'Başvuru Alındı' : 'Ödeme Başarısız'}</h2>
            {paymentOutcome === 'success' ? (
              <div style={{ marginTop: '20px', padding: '24px', background: '#fdf2f8', borderRadius: '12px', border: '1px solid #fbcfe8', textAlign: 'center' }}>
                <p style={{ color: '#0f172a', fontSize: '1.05rem', fontWeight: '600', marginBottom: '16px' }}>Kadın eğitim başvurunuz alınmıştır.<br />Üyelik işlemlerini tamamlamak için lütfen tesisimize geliniz.</p>
                <div style={{ padding: '16px', background: '#fff', borderRadius: '8px', border: '1px dashed #f9a8d4' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#334155', fontSize: '1rem' }}>Tesis Adresimiz</h4>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem', lineHeight: '1.5' }}>
                    Sakarya, Şht. Astsb. Kd. Çvş. Ali Ermercan Cd. İskenderun/Hatay
                  </p>
                </div>
              </div>
            ) : (
              <p className={`sa-result-message ${paymentOutcome === 'failed' ? 'error' : ''}`}>{resultMessage}</p>
            )}
            <div className="sa-action-row sa-card-result-actions">
              {paymentOutcome === 'failed' && (
                <button type="button" className="sa-submit-button sa-payment-action" data-variant="positive" onClick={() => { setPaymentOutcome(null); setStep(2); }}>Tekrar Dene</button>
              )}
              <button type="button" className="sa-btn-cancel" onClick={resetForm}>Yeni Başvuru</button>
            </div>
          </div>
        ) : paymentHtml ? (
          <div className="sa-card-iframe-card">
            <div className="sa-card-iframe-header">
              <button type="button" className="sa-card-iframe-back" onClick={() => { setPaymentHtml(null); setStep(2); }}>
                <FaArrowLeft /> Geri
              </button>
              <strong>{formatVatIncluded(totalAmount)}</strong>
            </div>
            <iframe ref={iframeRef} title="3D Secure Payment" srcDoc={paymentHtml} onLoad={handleIframeLoad} className="sa-card-iframe" />
          </div>
        ) : (
          <div className="sa-card-loading">Ödeme sayfası yükleniyor...</div>
        )}
      </div>

      {showTermsModal && (
        <div className="sa-terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="sa-terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sa-terms-modal-header">
              <h3>Tesis Kullanma Talimatı</h3>
              <button className="sa-terms-modal-close" onClick={() => setShowTermsModal(false)}>&times;</button>
            </div>
            <div className="sa-terms-modal-body">
              <div className="sa-terms-content">
                <center><strong>T.C. İSKENDERUN BELEDİYESİ<br />YARI OLİMPİK KAPALI YÜZME HAVUZU TESİS KULLANMA TALİMATI</strong></center>
                <br />
                <strong>GENEL KURALLLAR</strong>
                <ol>
                  <li>Havuza bonesiz, mayosuz, terliksiz ve duş alınmadan kesinlikle girilemez. Bu şartları sağlamayanlar havuz dışına çıkartılır.</li>
                  <li>Havuza girişte dezenfektanlı suya ayakların batırılması hijyen açısından zorunludur.</li>
                  <li>Havuz bölgesine, su dışında (matara) yiyecek ve içecek sokulması yasaktır.</li>
                  <li>Sporcu ve öğrencilerin, tribünde soyunup giyinmesi kesinlikle yasaktır.</li>
                  <li>Velilerin soyunma odasına girmesi kesinlikle yasaktır.</li>
                  <li>Açık ve kanamalı yarası, bandajı, kesiği, mantar gibi cilt hastalığı ve bulaşıcı hastalık hallerinde kesinlikle havuza girilemez.</li>
                  <li>Tesise cinsi ve niteliği ne olursa olsun hiçbir hayvanla girilemez.</li>
                  <li>Tesis sınırları içerisinde personel, üye ve misafirler dahil olmak üzere alkollü giriş yapılamayacağı gibi her türlü alkol ve sigara kullanımı yasaktır.</li>
                  <li>Yanlarında kıymetli evrak ve maddi değeri yüksek eşya vb. bulunduran kişiler bu eşyaların kaybından kendileri sorumludur. Kaybedilen hiçbir şeyden tesis yönetimi sorumlu tutulamaz.</li>
                  <li>Üye ve misafirler istenildiği takdirde ilgili personel ve güvenlik görevlilerine üye kartını ve kimliğini ibraz etmek zorundadır.</li>
                  <li>Üye ve misafirler yararlandığı spor branşına göre uygun ekipmanları bulundurmakla yükümlüdür. Aksi halde hizmetten faydalanmayı talep edemez.</li>
                  <li>Bayanların hizmetine açık olan gün ve saatlerde tribün seyirciye kapalıdır.</li>
                  <li>Görevi ve ünvanı ne olursa olsun, tesisten ve hizmetlerden bedelini ödemeden hiç kimse faydalanamaz.</li>
                  <li>Yüzme bilmeyenlerin antrenörsüz ve can kurtaransız havuza girmesi yasaktır.</li>
                  <li>Havuz merdivenlerine oturulamaz.</li>
                  <li>Üyeler ve kursiyerler mayo ile tribüne çıkamazlar.</li>
                  <li>Dışarıda kullanılan ayakkabı ve terlikle havuz çevresinde kesinlikle dolaşılmaz.</li>
                  <li>Havuz çevresinde koşmak, itişmek ve tehlike oluşturacak şekilde atlamak yasaktır.</li>
                  <li>On sekiz yaşından küçük çocuklar veli onayı olmaksızın yüzme havuzundan faydalanamaz.</li>
                  <li>Tesis kurallarına uymayan kişilerin üyelikleri iptal edilecektir. Ödenen ücretlerin geri iadesi yapılmayacaktır.</li>
                  <li>Eğitim ve çalışma saatinin belirlenmesi idare yetkisinde olup, idare bu eğitim saatlerini değiştirme ve iptal etme hakkına sahiptir.</li>
                  <li>Üyelik veya eğitim formunu doldurup imza atan tüm kullanıcılarımız aynı zamanda tesis kurallarına uyacağına dair imza atmış sayılır.</li>
                  <li>Kulvarın üzerine oturulmaz. Aksi belirtilmedikçe havuz enine kullanılamaz.</li>
                  <li>Değerli üyelerimiz ve kursiyerlerimizin daha sağlıklı, daha güvenli, daha temiz bir ortamda spor yapması için tesis kurallarına uyunuz, uymayanları uyarınız.</li>
                  <li>Kullanıcıların havuz içerisinde ve çevresinde bulunan malzemelerde meydana getirecekleri hasardan kendileri sorumludur.</li>
                  <li>Seyirciler havuz içerisine inemezler.</li>
                  <li>Eğitim seanslarında ilk 10 dakika içeride kalınabilir, sonrasında ebeveynler dışarıda beklemelidir.</li>
                  <li>Deniz malzemeleri (deniz topu, deniz yatağı, deniz simidi, kolluk) kullanmak yasaktır.</li>
                  <li>Genel temizlik günü havuz kapalıdır.</li>
                </ol>

                <strong>HAVUZ GİRİŞİ</strong>
                <ul>
                  <li>Dışarıda kullanılan ayakkabılarla havuz çevresine girilmesi yasaktır.</li>
                  <li>Havuz kullanımı öncesi duş alınması zorunludur. Duşlarda şampuan, sabun vb. malzeme kullanılamaz. Duş almadan havuzu kullanan bir kişi 20 m³ suyu kirletir, duş alırsa bu miktar 2 m³ suya düşer.</li>
                  <li>Havuza girmeden önce her türlü ziynet eşyasının (kolye, yüzük vb.) çıkarılması zorunludur.</li>
                  <li>Havuza girmeden önce varsa ağzınızdaki sakızı çıkarmanız can güvenliğiniz açısından önemlidir.</li>
                  <li>Havuz suyuna tükürmek, havuz suyu ile ağız ve burun temizlemek yasaktır.</li>
                  <li>Yüzme bilmeyenlerin, havuz eğitmenlerinin bilgisi dışında kullanması yasaktır.</li>
                </ul>

                <strong>HAVUZ ÇIKIŞINDA</strong>
                <ul>
                  <li>Bitim anonsuyla birlikte havuzu mümkün olduğunca çabuk boşaltınız ve kesinlikle tekrar havuza girmeyiniz.</li>
                  <li>Duşları fazla meşgul etmeyiniz ve soyunma odasını zamanında terk ediniz.</li>
                  <li>Çıkış esnasında havuzu terlikle terk ediniz ve içeride kesinlikle ayakkabı giymeyiniz.</li>
                </ul>

                <strong>SOYUNMA ODALARI KULLANMA TALİMATI</strong>
                <ul>
                  <li>Dolaplar sadece "Günlük Kullanım" içindir, çalışmanız bittikten sonra eşyalarınızı alınız.</li>
                  <li>Para veya değerli eşyalarınızı dolaplara bırakmayınız, bu konuda mesuliyet kabul edilmez.</li>
                  <li>Duşlarda 5 dakikadan fazla kalmayınız.</li>
                  <li>Musluk ve lambaları açık bırakmayınız.</li>
                  <li>Soyunma odalarında sigara içilmez, yiyecek, alkollü içecek ve sakız kullanılamaz.</li>
                  <li>Tesis ayrılırken kilitli bırakılan dolaplar gün sonunda açılır; içindeki eşyalar idare tarafından 15 gün muhafaza edilir, sonrasında hayır kurumuna bağışlanır. Unutulan terli ve ıslak eşyalar muhafaza edilmeden imha edilir.</li>
                  <li>Hijyen ve güvenlik gereği tüm eşyalar soyunma dolabına yerleştirilmeli, hiçbir eşya dolap dışında bırakılmamalıdır.</li>
                </ul>
              </div>
            </div>
            <div className="sa-terms-modal-footer">
              <button className="sa-submit-button" onClick={() => { setFormData({ ...formData, termsAccepted: true }); setShowTermsModal(false); }}>Okudum, Onaylıyorum</button>
              <button className="sa-btn-cancel" onClick={() => setShowTermsModal(false)}>Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WomenTrainingApplicationForm;
